import React, { useEffect } from 'react';

import { Box, Tooltip, useTheme } from '@material-ui/core';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { RiEyeLine } from 'react-icons/ri';
import { TbPencil } from 'react-icons/tb';
import { FormProvider } from 'react-hook-form';
import { noop } from 'lodash';

import { AppIconButton } from '@vyce/core/src/components/AppIconButton';
import { SafeDndProvider } from '@vyce/core/src/components/SafeDndProvider';
import { FormDoc } from '@vyce/core/src/types';

import { useCreateFormData } from './hooks/useCreateFormData';
import { FormToAnswer } from './components/FormToAnswer';
import { FormEditor } from './components/FormEditor';
import { FormSelectRecipients } from './components/FormSelectRecipients';
import { useFormRecipients } from './hooks/useFormRecipients';
import { Team, TimeAndAttendanceLocation, User } from '../../types';

interface Props {
  user: User;
  companyId: string;
  form?: FormDoc;
  locations: TimeAndAttendanceLocation[];
  teams?: Team[];
  isTemplate?: boolean;
  initialSelectRecipientsOpen?: boolean;
  initialFolderId?: string | null;
}

export const FormConstructor: React.FC<Props> = ({
  user,
  companyId,
  form,
  locations,
  teams = [],
  isTemplate = false,
  initialSelectRecipientsOpen = false,
  initialFolderId = null,
}) => {
  const {
    methods,
    addQuestion,
    onSelectRecipientsClick,
    handleRemoveQuestion,
    questions,
    findQuestion,
    moveQuestion,
    duplicateQuestion,
    previewOpen,
    setPreviewOpen,
    createForm,
    loading,
    updateForm,
    selectRecipientsOpen,
    disableSelectRecipients,
    setSelectRecipientsOpen,
    uploadFileToFirebaseStorage,
    onSendToResourceWorkers,
  } = useCreateFormData({ companyId, form, user, isTemplate, initialSelectRecipientsOpen });

  const {
    isPhoneInputDisabled,
    handleEmailFieldKeyDown,
    handlePhoneFieldKeyDown,
    addEmailToList,
    addPhoneToList,
    deleteContact,
    emails,
    phones,
    selectedWorkers,
    toggleWorker,
    setIsPhoneInputDisabled,
    bulkPaste,
    bulkLimitHint,
    recipientsFormMethods,
    sendFormRecipients,
  } = useFormRecipients({ user, companyId, form, initialFolderId });
  const theme = useTheme();

  useEffect(() => {
    if (form) {
      methods.reset(form);
    }
  }, [form]);

  return (
    <SafeDndProvider backend={HTML5Backend}>
      {(!form?.status || form?.status === 'draft') && (
        <Box
          style={{
            padding: 16,
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <Tooltip title={!previewOpen ? 'Preview' : 'Edit'}>
            <AppIconButton onClick={() => setPreviewOpen(!previewOpen)}>
              {!previewOpen ? <RiEyeLine size="20px" /> : <TbPencil size="20px" />}
            </AppIconButton>
          </Tooltip>
        </Box>
      )}

      {!isTemplate && selectRecipientsOpen ? (
        <FormProvider {...recipientsFormMethods}>
          <form onSubmit={recipientsFormMethods.handleSubmit(noop)}>
            <FormSelectRecipients
              companyId={companyId}
              userId={user.uuid}
              locations={locations}
              teams={teams}
              createForm={createForm}
              updateForm={updateForm}
              sendFormRecipients={sendFormRecipients}
              hideRecipientsStep={() => setSelectRecipientsOpen(false)}
              isPhoneInputDisabled={isPhoneInputDisabled}
              handleEmailFieldKeyDown={handleEmailFieldKeyDown}
              handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
              addEmailToList={addEmailToList}
              addPhoneToList={addPhoneToList}
              deleteContact={deleteContact}
              emails={emails}
              phones={phones}
              selectedWorkers={selectedWorkers}
              toggleWorker={toggleWorker}
              setIsPhoneInputDisabled={setIsPhoneInputDisabled}
              bulkPaste={bulkPaste}
              bulkLimitHint={bulkLimitHint}
              onSendToResourceWorkers={onSendToResourceWorkers}
            />
          </form>
        </FormProvider>
      ) : previewOpen ? (
        <Box display="flex" width="100%" justifyContent="center">
          <Box maxWidth={750} width="100%">
            <FormToAnswer form={methods.getValues()} user={user} />
          </Box>
        </Box>
      ) : (
        <FormEditor
          methods={methods}
          questions={questions}
          uploadFileToFirebaseStorage={uploadFileToFirebaseStorage}
          findQuestion={findQuestion}
          moveQuestion={moveQuestion}
          handleRemoveQuestion={handleRemoveQuestion}
          duplicateQuestion={duplicateQuestion}
          addQuestion={addQuestion}
          onSelectRecipientsClick={onSelectRecipientsClick}
          createForm={() => createForm(isTemplate ? {} : { status: 'draft', folderId: initialFolderId })}
          updateForm={(formId: string) => updateForm({ formId, status: 'draft' })}
          loading={loading}
          formId={form?.id}
          disableSelectRecipients={disableSelectRecipients}
          isTemplate={isTemplate}
        />
      )}
    </SafeDndProvider>
  );
};
